import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, extractError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { useAuth } from "../context/AuthContext";

export default function CycleNewPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const isPlatform = user?.role === "platform_admin";
    const [name, setName] = useState("");
    const [orgId, setOrgId] = useState(isPlatform ? "" : user?.org_id || "");
    const [orgs, setOrgs] = useState([]);
    const [loadingOrgs, setLoadingOrgs] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!isPlatform) return;
        setLoadingOrgs(true);
        api.get("/admin/organizations")
            .then((r) => setOrgs(r.data))
            .catch((e) => toast.error(extractError(e, "Could not load orgs")))
            .finally(() => setLoadingOrgs(false));
    }, [isPlatform]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!orgId) {
            toast.error("Select an organization");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/cycles", { org_id: orgId, name: name.trim() });
            toast.success("Cycle created");
            navigate(`/app/cycles/${data.id}`);
        } catch (err) {
            toast.error(extractError(err, "Could not create cycle"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-6 max-w-2xl" data-testid="cycle-new-page">
            <div>
                <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-2">
                    New assessment cycle
                </div>
                <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-[#0B2545] leading-tight">
                    Start a coherence audit
                </h1>
                <p className="mt-2 text-sm text-[#0E1B2C]/70">
                    A cycle creates one domain assessment for each of the six ESSS domains. Only the three
                    active domains can be scored in Phase 2.
                </p>
            </div>
            <form
                onSubmit={handleSubmit}
                className="rounded-2xl bg-white border border-[#D6E6F2] p-6 space-y-5"
                data-testid="cycle-new-form"
            >
                <div className="space-y-2">
                    <Label htmlFor="cycle-name">Cycle name</Label>
                    <Input
                        id="cycle-name"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Fall 2026 Coherence Audit"
                        data-testid="cycle-name-input"
                    />
                </div>
                <div className="space-y-2">
                    <Label>Organization</Label>
                    {isPlatform ? (
                        <Select value={orgId} onValueChange={setOrgId} disabled={loadingOrgs}>
                            <SelectTrigger data-testid="cycle-org-select">
                                <SelectValue placeholder={loadingOrgs ? "Loading…" : "Select organization"} />
                            </SelectTrigger>
                            <SelectContent>
                                {orgs.map((o) => (
                                    <SelectItem key={o.id} value={o.id}>
                                        {o.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <Input
                            value={user?.organization?.name || ""}
                            disabled
                            className="bg-[#D6E6F2]/50"
                            data-testid="cycle-org-locked"
                        />
                    )}
                </div>
                <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-11 rounded-full bg-[#0B2545] hover:bg-[#1E56A0] text-white font-semibold"
                    data-testid="cycle-submit-btn"
                >
                    {submitting ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
                    Create cycle
                </Button>
            </form>
        </div>
    );
}
